import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, X } from 'lucide-react';
import useGameStore from '../../store/gameStore';
import { CONTEXTUAL_TUTORIALS } from '../../data/contextualTutorials';
import MarkdownLite from '../MarkdownLite';

/**
 * Contextual tutorial card — single, blocking, one-shot per id. Rendered
 * globally from the Layout. The slices fire `triggerContextualTutorial(id)`
 * when the player first encounters a mechanic ; this card pops, the player
 * dismisses it, and the id is permanently flagged seen for the current game.
 *
 * Visual : centered modal with backdrop blur. Sober/pro tone (no avatar /
 * mascot) — just a sparkle icon, a title, 1-2 short paragraphs and a
 * single primary CTA. Optional secondary "Y aller" button when a target
 * route is suggested.
 */
export default function ContextualTutoCard() {
  const active = useGameStore(s => s.activeContextualTuto);
  const dismiss = useGameStore(s => s.dismissContextualTutorial);
  const navigate = useNavigate();

  if (!active) return null;
  const tuto = CONTEXTUAL_TUTORIALS[active.id];
  if (!tuto) {
    // Unknown id (defensive — shouldn't happen) → flush state.
    dismiss();
    return null;
  }

  const handlePrimary = () => {
    dismiss();
    if (tuto.route) navigate(tuto.route);
  };

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center p-4 animate-in fade-in duration-200"
      data-testid="contextual-tuto-card"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-ink-900/70 backdrop-blur-sm" onClick={dismiss} />

      {/* Card */}
      <div className="relative bg-ink-800 rounded-2xl border border-accent/40 shadow-2xl shadow-accent/10 max-w-md w-full overflow-hidden animate-in slide-in-from-bottom-4 duration-300">
        {/* Header band */}
        <div className="bg-gradient-to-r from-accent/20 to-ink-700 px-5 py-4 flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-accent/30 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-5 h-5 text-accent-foreground" data-testid="contextual-tuto-icon" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-wider text-accent-foreground/70">
              Découverte d&apos;une mécanique
            </p>
            <h3 className="text-base font-bold text-foreground leading-tight" data-testid="contextual-tuto-title">
              {tuto.title}
            </h3>
          </div>
          <button
            onClick={dismiss}
            className="text-slate-500 hover:text-slate-300 transition-colors flex-shrink-0"
            data-testid="contextual-tuto-close"
            aria-label="Fermer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-3" data-testid="contextual-tuto-body">
          {tuto.body.map((paragraph, i) => (
            <MarkdownLite
              key={`p-${i}`}
              text={paragraph}
              className="text-sm text-slate-300 leading-relaxed"
              strongClassName="text-foreground font-semibold"
            />
          ))}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-ink-600 bg-ink-700/30 flex items-center justify-end gap-2">
          <button
            onClick={handlePrimary}
            className="bg-accent text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-accent-hover transition-colors"
            data-testid="contextual-tuto-cta"
          >
            {tuto.cta}
          </button>
        </div>
      </div>
    </div>
  );
}
